// i18n
import { useTranslation } from 'react-i18next';

// types
import { Entity, Loan } from '../../../../@types';

// mui
import { Container, Grid, Typography } from '@mui/material';

// pages
import { CardLocation } from './CardLocation/CardLocation';

// cards
import { CardEntities } from '../../../cards/CardEntities/CardEntities';
import { CardLoans } from '../../../cards/CardLoans/CardLoans';
import { CardTraceability } from '../../../cards/CardTraceability/CardTraceability';
import { CardUtilization } from '../../../cards/CardUtilization/CardUtilization';
import { CardWallet } from '../../../cards/CardWallet/CardWallet';

// config
import { featureFlags } from '../../../../config/featureFlags';

// props
export interface DetailsProps {
	entity: Entity;
	isLoadingLoans?: boolean;
	isLoadingTraceability?: boolean;
	isLoadingUtilization?: boolean;
	loans: Loan[];
}

export const Details = ({
	entity,
	isLoadingLoans,
	isLoadingTraceability,
	isLoadingUtilization,
	loans,
}: DetailsProps) => {
	// hooks
	const { t } = useTranslation();

	const colWidthLoans = {
		xs: 12,
		md: 6,
		lg: 4,
	};

	const colWidthUtilization = {
		xs: 12,
		md: 6,
		lg: 4,
	};

	const colWidthTraceability = {
		xs: 12,
		md: 6,
		lg: 4,
	};

	const colWidthEntities = {
		xs: 12,
		md: 6,
		lg: 4,
	};

	if (featureFlags['FINANCIAL_TRACEABILITY']) {
		colWidthLoans.lg = 3;
		colWidthUtilization.lg = 3;
		colWidthTraceability.lg = 3;
	}

	// vars
	const associatedEntities: Entity[] = [];

	// for each loan
	for (let i = 0; i < loans.length; i++) {
		const entityLoan = loans[i].entities.find((e) => e.id === entity.id);

		if (entityLoan && entityLoan.parent) {
			const entityExists = associatedEntities.find((e) => e.id === entityLoan.parent?.id);

			if (!entityExists) {
				associatedEntities.push(entityLoan.parent);
			}
		}
	}

	return (
		<Container maxWidth={false}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography variant="h2">{t('details')}</Typography>
				</Grid>
				<Grid item {...colWidthLoans}>
					<CardLoans isLoading={isLoadingLoans} loans={loans} />
				</Grid>
				<Grid item {...colWidthUtilization}>
					<CardUtilization
						DialogProps={{
							subtitle: {
								children: entity.name,
							},
							type: 'ENTITY',
							utilization: entity.utilization,
						}}
						isLoading={isLoadingUtilization}
					/>
				</Grid>
				<Grid item {...colWidthTraceability}>
					<CardTraceability
						DialogProps={{
							subtitle: {
								children: entity.name,
							},
						}}
						isLoading={isLoadingTraceability}
						traceability={entity.traceability}
						type="TRANSACTIONS"
					/>
				</Grid>
				{featureFlags['FINANCIAL_TRACEABILITY'] && (
					<Grid item {...colWidthTraceability}>
						<CardTraceability
							DialogProps={{
								subtitle: {
									children: entity.name,
								},
							}}
							isLoading={isLoadingTraceability}
							traceability={entity.traceability}
							type="FINANCIAL"
						/>
					</Grid>
				)}
				<Grid item {...colWidthEntities}>
					<CardEntities entities={associatedEntities} />
				</Grid>
				<Grid item xs={12} md={6} lg={4}>
					<CardLocation entity={entity} />
				</Grid>
				<Grid item xs={12} md={true} lg={4}>
					<CardWallet wallet={entity.wallet} />
				</Grid>
			</Grid>
		</Container>
	);
};
